import { GENDERS, type SortOption } from '@accheyrishtey/shared';

export type ProfileGender = (typeof GENDERS)[number];
export type GenderViewValue = 'show_male' | 'show_female';
export type DashboardSortValue = SortOption | GenderViewValue;

export const GENDER_VIEW_OPTIONS: ReadonlyArray<{
  value: GenderViewValue;
  label: string;
  gender: ProfileGender;
}> = [
  { value: 'show_male', label: 'Male', gender: 'Male' },
  { value: 'show_female', label: 'Female', gender: 'Female' },
];

export const THIRTY_PLUS_OPTIONS: ReadonlyArray<{
  label: string;
  gender: ProfileGender;
}> = [
  { label: '30+ female', gender: 'Female' },
  { label: '30+ male', gender: 'Male' },
];

function genderForView(value: string): ProfileGender | undefined {
  return GENDER_VIEW_OPTIONS.find((option) => option.value === value)?.gender;
}

/**
 * The selector contains two "show only" actions alongside real sort orders.
 * Deriving its value from the URL keeps it controlled without adding a second
 * source of truth for dashboard filters.
 */
export function dashboardSortValue(params: URLSearchParams): DashboardSortValue {
  const explicitSort = params.get('sort');
  if (explicitSort) return explicitSort as SortOption;

  const genders = params.getAll('gender');
  if (genders.length === 1 && genders[0] === 'Male') return 'show_male';
  if (genders.length === 1 && genders[0] === 'Female') return 'show_female';
  return 'newest';
}

/** Apply a real sort order or one of the gender-only choices from the selector. */
export function applyDashboardSortValue(
  params: URLSearchParams,
  value: DashboardSortValue,
): URLSearchParams {
  const next = new URLSearchParams(params);
  const gender = genderForView(value);
  next.delete('page');

  if (gender) {
    next.delete('gender');
    next.append('gender', gender);
    // A gender choice is a view preset, not an ORDER BY clause. Reset it to
    // the stable newest-first order supplied by the API.
    next.delete('sort');
    return next;
  }

  // With one gender selected, write even the default sort explicitly.
  // Otherwise choosing "Newest" from any other order would immediately make
  // the derived selector value snap back to Male/Female.
  const hasSingleGender = params.getAll('gender').length === 1;
  if (value === 'newest' && !hasSingleGender) next.delete('sort');
  else next.set('sort', value);
  return next;
}

export function isThirtyPlusPreset(params: URLSearchParams, gender: ProfileGender): boolean {
  const genders = params.getAll('gender');
  return genders.length === 1
    && genders[0] === gender
    && params.get('age_min') === '30'
    && !params.has('age_max');
}

/** Toggle an exact 30-and-over gender preset while preserving unrelated filters. */
export function toggleThirtyPlusPreset(
  params: URLSearchParams,
  gender: ProfileGender,
): URLSearchParams {
  const next = new URLSearchParams(params);
  const wasActive = isThirtyPlusPreset(params, gender);
  next.delete('page');
  next.delete('gender');
  next.delete('age_min');

  if (!wasActive) {
    next.append('gender', gender);
    next.set('age_min', '30');
    // "30+" has no upper age boundary, even if the detailed filter had one.
    next.delete('age_max');
  }
  return next;
}
